/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createAxiosInstance } from '../utils/Axios';
import { toast } from 'react-toastify';
import { useAuth, hasModuleAccess, hasSectionAccess } from '../utils/hooks/useAuth';

export default function CrmAccounts() {
  const { access, loading: authLoading } = useAuth();

  // Check module and section permissions
  const hasCrmAccess = access ? hasModuleAccess('iurm', access) : false;
  const canViewAccounts = access ? hasSectionAccess('iurm', 'Accounts', 'view', access) : false;
  const canCreateAccount = access ? hasSectionAccess('iurm', 'Accounts', 'create', access) : false;
  const canEditAccount = access ? hasSectionAccess('iurm', 'Accounts', 'edit', access) : false;
  const canDeleteAccount = access ? hasSectionAccess('iurm', 'Accounts', 'delete', access) : false;

  // Check other CRM section permissions for tabs
  const canViewOverview = access ? hasSectionAccess('iurm', 'Overview', 'view', access) : false;
  const canViewLeads = access ? hasSectionAccess('iurm', 'Leads', 'view', access) : false;
  const canViewContacts = access ? hasSectionAccess('iurm', 'Contacts', 'view', access) : false;
  const canViewTasks = access ? hasSectionAccess('iurm', 'Tasks', 'view', access) : false;
  const canViewCampaigns = access ? hasSectionAccess('iurm', 'Campaigns', 'view', access) : false;
  const canViewIntegration = access ? hasSectionAccess('iurm', 'Integration', 'view', access) : false;

  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [selectedAccount, setSelectedAccount] = useState(null);
  
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [stats, setStats] = useState({
    totalAccounts: 0,
    activeAccounts: 0,
    totalContacts: 0,
    totalRevenue: 0
  });

  // Modal States
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [newAccount, setNewAccount] = useState({ name: '', industry: '', website: '', revenue: '', owner: '' });
  const [isSavingAccount, setIsSavingAccount] = useState(false);

  const [showEditAccountModal, setShowEditAccountModal] = useState(false);
  const [editAccountData, setEditAccountData] = useState(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteAccountId, setDeleteAccountId] = useState(null);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const [openDropdownId, setOpenDropdownId] = useState(null);

  useEffect(() => {
    console.log('CRM Accounts Permissions:', {
      hasCrmAccess,
      canViewAccounts,
      canCreateAccount,
      canEditAccount,
      canDeleteAccount
    });
  }, [hasCrmAccess, canViewAccounts, canCreateAccount, canEditAccount, canDeleteAccount]);

  useEffect(() => {
    const fetchAccounts = async () => {
      setIsLoading(true);
      try {
        const axiosInstance = createAxiosInstance();
        const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
        
        let queryParams = `?limit=100`;
        if (searchTerm) queryParams += `&search=${searchTerm}`;

        const { data } = await axiosInstance.get(`${apiUrl}/accounts${queryParams}`);
        if (data && data.success) {
          setAccounts(data.accounts || []);
          if (data.stats) setStats(data.stats);
        }
      } catch (error) {
        console.error("Failed to fetch accounts", error);
        toast.error("Failed to load accounts");
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      fetchAccounts();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm, refreshTrigger]);

  const handleSaveAccount = async () => {
    if (!newAccount.name) {
      toast.error("Account Name is required");
      return;
    }
    setIsSavingAccount(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.post(`${apiUrl}/accounts`, newAccount);
      
      toast.success("Account created successfully!");
      setShowAddAccountModal(false);
      setNewAccount({ name: '', industry: '', website: '', revenue: '', owner: '' });
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Save failed", error);
      toast.error(error?.response?.data?.message || "Failed to create account");
    } finally {
      setIsSavingAccount(false);
    }
  };

  const handleUpdateAccount = async () => {
    if (!editAccountData.name) {
      toast.error("Account Name is required");
      return;
    }
    setIsSavingEdit(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.put(`${apiUrl}/accounts/${editAccountData._id}`, editAccountData);
      
      toast.success("Account updated successfully!");
      setShowEditAccountModal(false);
      if (selectedAccount?._id === editAccountData._id) {
        setSelectedAccount({ ...selectedAccount, ...editAccountData });
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Update failed", error);
      toast.error(error?.response?.data?.message || "Failed to update account");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deleteAccountId) return;
    setIsDeletingAccount(true);
    try {
      const axiosInstance = createAxiosInstance();
      const apiUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api';
      
      await axiosInstance.delete(`${apiUrl}/accounts/${deleteAccountId}`);
      
      toast.success("Account deleted successfully!");
      setShowDeleteAccountModal(false);
      if (selectedAccount?._id === deleteAccountId) {
        setSelectedAccount(null);
      }
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Delete failed", error);
      toast.error(error?.response?.data?.message || "Failed to delete account");
    } finally {
      setIsDeletingAccount(false);
      setDeleteAccountId(null);
    }
  };

  // Show loading while checking permissions
  if (authLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
        Loading permissions...
      </div>
    );
  }

  // Show access denied if no module access or no accounts view access
  if (!hasCrmAccess || !canViewAccounts) {
    return (
      <div style={{ 
        padding: '60px 40px', 
        textAlign: 'center',
        backgroundColor: '#fff',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        margin: '20px'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
        <h2 style={{ color: '#111827', marginBottom: '12px' }}>Access Denied</h2>
        <p style={{ color: '#6B7280', fontSize: '16px', marginBottom: '8px' }}>
          You don't have permission to access the Accounts section.
        </p>
        <p style={{ color: '#9CA3AF', fontSize: '14px' }}>
          Please contact your administrator to request access.
        </p>
      </div>
    );
  }

  return (
    <div className="crm-accounts-container" style={{ padding: '16px', backgroundColor: '#f8f9fa', minHeight: '100%', fontFamily: 'Inter, sans-serif', position: 'relative' }}>
      
      {/* Sub-Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #E5E7EB', marginBottom: '20px', paddingBottom: '0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          {canViewOverview && (
            <Link to="/crm/overview" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
                Overview
              </div>
            </Link>
          )}
          {canViewLeads && (
            <Link to="/crm/leads" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                Leads
              </div>
            </Link>
          )}
          {canViewContacts && (
            <Link to="/crm/contacts" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                Contacts
              </div>
            </Link>
          )}
          <div className="nav-item active" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', marginBottom: '8px', backgroundColor: '#8B5CF6', color: '#fff' }}>
            <div className="nav-dot" style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: 'currentColor' }}></div>
            Accounts
          </div>
          {canViewTasks && (
            <Link to="/crm/tasks" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>
                Tasks
              </div>
            </Link>
          )}
          {canViewCampaigns && (
            <Link to="/crm/campaigns" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"></path></svg>
                Campaigns
              </div>
            </Link>
          )}
          {canViewIntegration && (
            <Link to="/crm/integration" style={{ textDecoration: 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#9CA3AF', fontSize: '14px', fontWeight: '500', paddingBottom: '12px', borderBottom: '2px solid transparent', cursor: 'pointer' }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 16V7a2 2 0 0 0-2-2h-4"></path><path d="M4 16V7a2 2 0 0 1 2-2h4"></path><rect x="9" y="15" width="6" height="6" rx="1"></rect><line x1="12" y1="5" x2="12" y2="15"></line></svg>
                MCP Integration
              </div>
            </Link>
          )}
        </div>
      </div>

      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', margin: '0', color: '#111827', letterSpacing: '-0.5px' }}>Accounts</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button style={{ padding: '8px 16px', backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '8px', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Export</button>
          {canCreateAccount && (
            <button onClick={() => setShowAddAccountModal(true)} style={{ padding: '8px 20px', backgroundColor: '#5B21B6', border: 'none', borderRadius: '8px', fontWeight: '600', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>+ New Account</button>
          )}
        </div>
      </div>
      {isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>Loading Accounts...</div>
      ) : (
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Name</th>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Industry</th>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Website</th>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Revenue</th>
                <th style={{ padding: '16px 24px', color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {accounts.length > 0 ? accounts.map((acc, idx) => (
                <tr key={acc._id || idx} style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#111827', fontWeight: '500' }}>{acc.name}</td>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4B5563' }}>{acc.industry || '-'}</td>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#3B82F6' }}>{acc.website || '-'}</td>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4B5563' }}>{acc.revenue ? "$" + acc.revenue : '-'}</td>
                  <td style={{ padding: '16px 24px', fontSize: '14px', color: '#4B5563', textAlign: 'right' }}>
                    {canEditAccount && (
                      <button style={{ border: 'none', background: 'none', color: '#6B7280', cursor: 'pointer', marginRight: '12px' }} onClick={() => { setEditAccountData(acc); setShowEditAccountModal(true); }}>Edit</button>
                    )}
                    {canDeleteAccount && (
                      <button style={{ border: 'none', background: 'none', color: '#EF4444', cursor: 'pointer' }} onClick={() => { setDeleteAccountId(acc._id); setShowDeleteAccountModal(true); }}>Delete</button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="5" style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>No accounts found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {showAddAccountModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ backgroundColor: '#fff', padding: '24px', borderRadius: '12px', width: '400px' }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Add Account</h3>
            <input placeholder="Name" value={newAccount.name} onChange={e => setNewAccount({...newAccount, name: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <input placeholder="Industry" value={newAccount.industry} onChange={e => setNewAccount({...newAccount, industry: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <input placeholder="Website" value={newAccount.website} onChange={e => setNewAccount({...newAccount, website: e.target.value})} style={{ width: '100%', padding: '8px', marginBottom: '12px', border: '1px solid #D1D5DB', borderRadius: '6px' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setShowAddAccountModal(false)} style={{ padding: '8px 16px', background: '#fff', border: '1px solid #D1D5DB', borderRadius: '6px', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveAccount} disabled={isSavingAccount} style={{ padding: '8px 16px', background: '#5B21B6', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}